import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const ClickHandler = ({ onMapClick }) => {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
};

const FlyToPosition = ({ position }) => {
  const map = useMap();
  const prevRef = useRef(null);
  useEffect(() => {
    if (!position) return;
    const key = `${position[0]},${position[1]}`;
    if (prevRef.current === key) return;
    prevRef.current = key;
    map.flyTo(position, 15);
  }, [position, map]);
  return null;
};

const BasketLocationModal = ({ basket, onClose, onConfirm }) => {
  const defaultLat = typeof basket.latitude === 'number' ? basket.latitude : 37.5665;
  const defaultLng = typeof basket.longitude === 'number' ? basket.longitude : 126.9780;

  const [lat, setLat] = useState(defaultLat);
  const [lng, setLng] = useState(defaultLng);
  const [distance, setDistance] = useState(basket.distance != null ? String(basket.distance) : '');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [flyTo, setFlyTo] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const circleRadius = parseFloat(distance) > 0 ? parseFloat(distance) : 0;

  const handleMapClick = ({ lat: clickLat, lng: clickLng }) => {
    setLat(clickLat);
    setLng(clickLng);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    setSearchResults([]);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=5&addressdetails=1&accept-language=ko,en`
      );
      const data = await res.json();
      setSearchResults(data);
      if (data.length > 0) {
        const top = data[0];
        const newLat = parseFloat(top.lat);
        const newLng = parseFloat(top.lon);
        setLat(newLat);
        setLng(newLng);
        setFlyTo([newLat, newLng]);
      }
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectResult = (result) => {
    const newLat = parseFloat(result.lat);
    const newLng = parseFloat(result.lon);
    setLat(newLat);
    setLng(newLng);
    setFlyTo([newLat, newLng]);
    setSearchResults([]);
    setSearchQuery(result.display_name);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1050,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'white', borderRadius: '12px',
        width: '720px', maxWidth: '95vw',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      }}>
        {/* 헤더 */}
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #dee2e6',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#f8f9fa', borderRadius: '12px 12px 0 0',
        }}>
          <h5 style={{ margin: 0 }}>박 위치 수정 — {basket.basketName}</h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>✕</button>
        </div>

        {/* 검색 */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #dee2e6', position: 'relative', zIndex: 9999 }}>
          <div className="d-flex gap-2">
            <input
              className="form-control"
              placeholder="주소 또는 장소명 검색 (한글/영어)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className="btn btn-primary" style={{ whiteSpace: 'nowrap' }} onClick={handleSearch} disabled={isSearching}>
              {isSearching ? '검색 중…' : '검색'}
            </button>
          </div>
          {searchResults.length > 0 && (
            <div style={{
              position: 'absolute', top: 'calc(100% - 1px)', left: '16px', right: '16px',
              background: 'white', border: '1px solid #dee2e6', borderRadius: '0 0 8px 8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 99999,
              maxHeight: '210px', overflowY: 'auto',
            }}>
              {searchResults.map((r, i) => (
                <div
                  key={i}
                  style={{ padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: '13px' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f0f4ff'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  onClick={() => handleSelectResult(r)}
                >
                  {r.display_name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 지도 */}
        <div style={{ height: '420px', position: 'relative' }}>
          <MapContainer
            center={[defaultLat, defaultLng]}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <ClickHandler onMapClick={handleMapClick} />
            <FlyToPosition position={flyTo} />
            <Marker position={[lat, lng]} />
            {circleRadius > 0 && (
              <Circle
                center={[lat, lng]}
                radius={circleRadius}
                pathOptions={{ color: '#0d6efd', weight: 2, fillColor: '#0d6efd', fillOpacity: 0.3 }}
              />
            )}
          </MapContainer>
        </div>

        {/* 좌표 + 범위 */}
        <div style={{
          padding: '12px 16px', borderTop: '1px solid #dee2e6',
          background: '#f8f9fa',
          display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap',
        }}>
          <span className="small"><strong>위도:</strong> {lat.toFixed(6)}</span>
          <span className="small"><strong>경도:</strong> {lng.toFixed(6)}</span>
          <div className="d-flex align-items-center gap-2 ms-auto">
            <label className="mb-0 small fw-bold text-nowrap">범위</label>
            <input
              type="number"
              className="form-control form-control-sm"
              style={{ width: '110px' }}
              placeholder="미터 입력"
              min="0"
              value={distance}
              onChange={(e) => setDistance(e.target.value)}
            />
            <span className="small text-muted">m</span>
          </div>
        </div>

        {/* 푸터 */}
        <div style={{
          padding: '12px 16px', borderTop: '1px solid #dee2e6',
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
        }}>
          <button className="btn btn-secondary" onClick={onClose}>취소</button>
          <button
            className="btn btn-primary"
            onClick={() => onConfirm({ lat, lng, distance: parseFloat(distance) || 0 })}
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
};

export default BasketLocationModal;
